// =============================================================
// SEO — automatic generation with manual override per page
// =============================================================
//
// Contract:
//   - Given a page (or static section) and a locale, produce the
//     meta tags that should live in the document <head>.
//   - If a manual override exists and is enabled, use it.
//   - Otherwise, derive title from the page title and description
//     from the first paragraph of the body (Markdown-light strip).
//   - Default OG image is the site favicon (/favicon.svg).

import type { Locale } from './i18n';
import type { PageRecord, SeoOverride } from './content';
import { t } from './i18n';

export const DEFAULT_OG_IMAGE = '/og-default.png';
export const SITE_NAME = '333 Studio Budapest';

/** Canonical production origin. Used to build absolute canonical /
 *  og:url URLs. Kept in sync with the static `<link rel="canonical">`
 *  in index.html. */
export const SITE_ORIGIN = 'https://333studio.hu';

/** Resolve a possibly-relative URL against the current document's
 *  origin. Falls back to the given absolute URL when `window` is
 *  unavailable (SSR / non-browser context). Used so og:image / og:url
 *  always point at the SAME origin that served the HTML, regardless
 *  of whether the page is being served from the production domain
 *  (https://333studio.hu/) or from a Pages preview URL like
 *  https://szabi-1i7.pages.dev/. */
export const resolveAssetUrl = (maybeRelative: string, fallback?: string): string => {
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  if (typeof window !== 'undefined' && window.location?.origin) {
    try { return new URL(maybeRelative, window.location.origin).toString(); }
    catch { /* fall through */ }
  }
  return fallback ?? maybeRelative;
};

export interface SeoInput {
  page: Pick<PageRecord, 'id' | 'slug' | 'navLabel' | 'body' | 'seo'> | null;
  locale: Locale;
  /** An optional fallback title (e.g. for static sections). */
  fallbackTitle?: string;
  fallbackDescription?: string;
  canonicalPath?: string;
}

export interface SeoTags {
  title: string;
  description: string;
  ogImage: string;
  canonical: string;
}

const stripMarkdown = (s: string): string =>
  s
    .replace(/^#+\s*/gm, '')           // headings
    .replace(/[*_~`]+/g, '')           // emphasis markers
    .replace(/!\[[^\]]*]\([^)]*\)/g, '') // images
    .replace(/\[([^\]]+)]\([^)]*\)/g, '$1') // links
    .replace(/^>\s*/gm, '')            // blockquotes
    .replace(/\s+/g, ' ')
    .trim();

const truncate = (s: string, n: number): string =>
  s.length <= n ? s : s.slice(0, n - 1).trimEnd() + '…';

/** Build SEO tags for a page. */
export function buildSeo(input: SeoInput): SeoTags {
  const { page, locale, fallbackTitle, fallbackDescription, canonicalPath } = input;
  const override: SeoOverride | undefined = page?.seo?.enabled ? page.seo : undefined;

  // Title
  const title = (override?.perLocale?.[locale]?.title)
    ?? override?.title
    ?? page?.navLabel[locale]
    ?? fallbackTitle
    ?? t('site.name');

  // Description
  const rawDesc = (override?.perLocale?.[locale]?.description)
    ?? override?.description
    ?? (page?.body[locale] ? stripMarkdown(page.body[locale]) : '')
    ?? fallbackDescription
    ?? t('about.lede');
  const description = truncate(rawDesc, 160);

  // OG image
  const ogImage = override?.perLocale?.[locale]?.ogImage
    ?? override?.ogImage
    ?? DEFAULT_OG_IMAGE;

  // Canonical URL — always absolute so crawlers don't have to resolve
  // it against an arbitrary document URL.
  const path = canonicalPath ?? (page?.slug ? `/${page.slug}` : '/');
  const canonical = new URL(path, SITE_ORIGIN).toString();

  return { title, description, ogImage, canonical };
}

// -----------------------------------------------------------
// <head> injection — used by the client at runtime so SEO
// updates flow through when admins edit content without a
// rebuild. Cloudflare Pages Functions can also call this same
// helper for SSR-style rendering.
// -----------------------------------------------------------

interface MetaSpec {
  attr: 'name' | 'property' | 'rel';
  key: string;
  value: string;
}

const setOrCreate = (spec: MetaSpec): HTMLMetaElement | HTMLLinkElement => {
  const selector =
    spec.attr === 'rel'
      ? `link[rel="${spec.key}"]`
      : `${spec.attr === 'property' ? 'meta[property' : 'meta[name'}="${spec.key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!el) {
    if (spec.attr === 'rel') {
      el = document.createElement('link');
      el.setAttribute('rel', spec.key);
    } else {
      el = document.createElement('meta');
      el.setAttribute(spec.attr, spec.key);
    }
    document.head.appendChild(el);
  }
  if (spec.attr === 'rel') {
    (el as HTMLLinkElement).setAttribute('href', spec.value);
  } else {
    (el as HTMLMetaElement).setAttribute('content', spec.value);
  }
  return el;
};

export function applySeoToHead(tags: SeoTags): void {
  document.title = tags.title;

  // Anchor og:image to the document's own origin. This is what lets the
  // same build serve OG cards correctly from any preview URL or from the
  // production custom domain once it's bound.
  const ogImageAbs = resolveAssetUrl(tags.ogImage);

  const specs: MetaSpec[] = [
    { attr: 'name', key: 'description', value: tags.description },
    { attr: 'property', key: 'og:title', value: tags.title },
    { attr: 'property', key: 'og:description', value: tags.description },
    { attr: 'property', key: 'og:image', value: ogImageAbs },
    { attr: 'property', key: 'og:image:secure_url', value: ogImageAbs },
    { attr: 'property', key: 'og:image:type', value: 'image/png' },
    { attr: 'property', key: 'og:image:width', value: '1200' },
    { attr: 'property', key: 'og:image:height', value: '630' },
    { attr: 'property', key: 'og:image:alt', value: SITE_NAME },
    { attr: 'property', key: 'og:type', value: 'website' },
    { attr: 'property', key: 'og:site_name', value: SITE_NAME },
    { attr: 'property', key: 'og:url', value: tags.canonical },
    { attr: 'property', key: 'og:locale', value: 'hu_HU' },
    { attr: 'name', key: 'twitter:card', value: 'summary_large_image' },
    { attr: 'name', key: 'twitter:title', value: tags.title },
    { attr: 'name', key: 'twitter:description', value: tags.description },
    { attr: 'name', key: 'twitter:image', value: ogImageAbs },
    { attr: 'name', key: 'twitter:image:alt', value: SITE_NAME },
  ];
  specs.forEach(setOrCreate);
  setOrCreate({ attr: 'rel', key: 'canonical', value: tags.canonical });
}

/** Plain render of `<title>` + meta tags as a string — used by Pages Functions for SSR. */
export function renderSeoTags(tags: SeoTags): string {
  const esc = (s: string) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return [
    `<title>${esc(tags.title)}</title>`,
    `<meta name="description" content="${esc(tags.description)}">`,
    `<meta property="og:title" content="${esc(tags.title)}">`,
    `<meta property="og:description" content="${esc(tags.description)}">`,
    `<meta property="og:image" content="${esc(tags.ogImage)}">`,
    `<meta property="og:image:secure_url" content="${esc(tags.ogImage)}">`,
    `<meta property="og:image:type" content="image/png">`,
    `<meta property="og:image:width" content="1200">`,
    `<meta property="og:image:height" content="630">`,
    `<meta property="og:image:alt" content="${esc(SITE_NAME)}">`,
    `<meta property="og:type" content="website">`,
    `<meta property="og:site_name" content="${esc(SITE_NAME)}">`,
    `<meta property="og:url" content="${esc(tags.canonical)}">`,
    `<meta property="og:locale" content="hu_HU">`,
    `<meta name="twitter:card" content="summary_large_image">`,
    `<meta name="twitter:title" content="${esc(tags.title)}">`,
    `<meta name="twitter:description" content="${esc(tags.description)}">`,
    `<meta name="twitter:image" content="${esc(tags.ogImage)}">`,
    `<meta name="twitter:image:alt" content="${esc(SITE_NAME)}">`,
    `<link rel="canonical" href="${esc(tags.canonical)}">`,
  ].join('\n    ');
}